import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import { formatHyphenatedDateOnly } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ReactElement, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';

import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { getAllCatalogs } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { EmptyCoverLot } from '@/types/CoverLot';
import { GeoArea } from '@/types/GeoArea';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { slugToTitleCase } from '@/utils/strings';
import { getAuctionsNearMePageUrl, getCatalogUrl, getMediumItemImageUrl, mobileLogo } from '@/utils/urls';

import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';

export type AuctionsNearMePageMetaProps = {
    catalogsNearMe?: CatalogNearMe[];
    geoArea?: GeoArea;
    routeParams: AuctionsNearMeRouteParams;
    searchTerm?: string;
};
type EventSchemaData = {
    catalogId: number;
    city: string;
    country: string;
    description: string;
    houseName: string;
    key: string;
    state: string;
    title: string;
};

const AuctionsNearMePageHeaderMeta = ({
    catalogsNearMe = [],
    geoArea,
    routeParams,
    searchTerm,
}: AuctionsNearMePageMetaProps): ReactElement => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const baseUrl = useAppSelector(getBaseUrl, isEqual);
    const catalogsById = useAppSelector(getAllCatalogs);

    const pageParam = new URLSearchParams(search).get('page');
    const pageSuffix = pageParam ? `?page=${pageParam}` : '';

    // Format location from route params when navigating to locations not in predefined geo areas
    // via Related Geo Areas ("More Locations" in UI). (e.g., "Patchogue, NY")
    const locationFromUrl = useMemo(() => {
        if ('city' in routeParams || 'state' in routeParams) {
            const { city, state } = routeParams as { city?: string; state?: string };
            const decodedCity = city ? decodeURIComponent(city) : undefined;
            const decodedState = state ? decodeURIComponent(state) : undefined;
            const formattedCity = decodedCity ? slugToTitleCase(decodedCity) : undefined;
            const formattedState = decodedState?.toUpperCase();

            if (formattedCity && formattedState) {
                return `${formattedCity}, ${formattedState}`;
            }
            return formattedCity || formattedState;
        }
        return undefined;
    }, [routeParams]);

    const location = geoArea?.geoAreaName || locationFromUrl || searchTerm;

    const title = useAddNotificationCountToTitle(
        location
            ? formatMessage({ id: 'near_me_page_title_with_location' }, { location })
            : formatMessage({ id: 'near_me_page_title' })
    );

    const description = location
        ? formatMessage({ id: 'near_me_page_description_with_location' }, { location })
        : formatMessage({ id: 'near_me_page_description' });

    // Build canonical URL - pass full geoArea object for new URL format, or construct from route params.
    // Append ?page=N when present so paginated URLs are self-referencing rather than canonicalizing
    // to the parent, which would drop paginated content from Google's index.
    const canonical = useMemo(() => {
        if (geoArea) {
            return `${baseUrl}${getAuctionsNearMePageUrl(geoArea)}${pageSuffix}`;
        }

        if ('city' in routeParams && 'state' in routeParams && routeParams.city && routeParams.state) {
            return `${baseUrl}/find-auctions-near-me/${routeParams.state}/${routeParams.city}/${pageSuffix}`;
        }
        if ('state' in routeParams && routeParams.state) {
            return `${baseUrl}/find-auctions-near-me/${routeParams.state}/${pageSuffix}`;
        }
        if ('country' in routeParams && routeParams.country) {
            return `${baseUrl}/find-auctions-near-me/${routeParams.country}/${pageSuffix}`;
        }

        return `${baseUrl}${getAuctionsNearMePageUrl()}${pageSuffix}`;
    }, [baseUrl, geoArea, routeParams, pageSuffix]);

    const eventSchemaData: EventSchemaData[] = catalogsNearMe
        .filter((catalog) => Boolean(catalogsById[catalog.id]))
        .map((catalog) => ({
            catalogId: catalog.id,
            city: catalog.city,
            country: catalog.countryCode,
            description: catalog.description,
            houseName: catalog.houseDetails.name,
            key: `${catalog.id}`,
            state: catalog.state,
            title: catalog.title,
        }));

    const auctionHitsMetaData = eventSchemaData.map((event) => {
        const { address, coverLots, saleEndTs, saleStartTs } = catalogsById[event.catalogId];
        const { address1, address2, postalCode } = address;
        const displayAddress = address2 ? `${address1} ${address2}` : `${address1}`;
        const [first = EmptyCoverLot] = coverLots;
        const imageUrl =
            getMediumItemImageUrl(event.catalogId, event.catalogId, first.itemId, 1, first.imageVersion) || mobileLogo;

        return (
            <script
                key={event?.key}
                type="application/ld+json"
            >
                {`
        {
            "@context": "https://schema.org",
            "@type": "Event",
            "description": "${escapeQuotes(event?.description)}",
            "endDate": "${formatHyphenatedDateOnly(saleEndTs)}",
            "image": "${imageUrl}",
            "location": {
                "@type": "Place",
                "address": {
                    "@type": "PostalAddress",
                    "addressCountry": "${event.country}",
                    "addressLocality": "${event.city}",
                    "addressRegion": "${event.state}",
                    "streetAddress": "${escapeQuotes(displayAddress)}",
                    "postalCode": "${postalCode}"
                },
                "name": "${event.houseName}"
            },
            "name": "${escapeQuotes(event.title)}",
            "organizer": {
                "@type": "Organization",
                "address": {
                    "@type": "PostalAddress",
                    "addressCountry": "${event.country}",
                    "addressLocality": "${event.city}",
                    "addressRegion": "${event.state}",
                    "streetAddress": "${displayAddress}",
                    "postalCode": "${postalCode}"
                },
                "name": "${event.houseName}"
            },
            "startDate": "${formatHyphenatedDateOnly(saleStartTs)}",
            "url": "${getCatalogUrl(event.catalogId, event.title)}"
        }
    `}
            </script>
        );
    });

    // Custom search pages (e.g. /find-auctions-near-me?search=Cleveland) should not be indexed by search engines
    const isCustomSearch = Boolean(searchTerm);

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            title={title}
        >
            {isCustomSearch && (
                <meta
                    content="noindex, follow"
                    name="robots"
                />
            )}
            {auctionHitsMetaData}
        </PageHeaderMeta>
    );
};

export default AuctionsNearMePageHeaderMeta;
